import { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShieldOff, Trash2 } from "lucide-react";
import { Route as rootRoute } from "./__root";
import { orpc } from "@/frontend/orpc-client";
import { Button } from "@/frontend/components/ui/button";
import { PortalSignIn } from "@/frontend/components/delete-patient-data/PortalSignIn";
import { FacilityList } from "@/frontend/components/delete-patient-data/FacilityList";
import { ConfirmActionDialog } from "@/frontend/components/delete-patient-data/ConfirmActionDialog";
import {
  deriveConfirmAction,
  isFacilityActionable,
  signInErrorFromUrl,
} from "@/frontend/utils/delete-patient-data";
import type { ConfirmAction } from "@/frontend/types/delete-patient-data";
import { useDeletePatientDataMutations } from "@/frontend/mutations/delete-patient-data";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/delete-your-data",
  component: DeleteYourDataPage,
});

/**
 * The public `/delete-your-data` page. A patient signs in at any one portal they
 * have connected before, and that single sign-in resolves to every workspace
 * holding their records, with no Medblocks account involved. They tick what they
 * mean and act on it once, scoped as tightly as the selection allows.
 */
export function DeleteYourDataPage() {
  const [confirming, setConfirming] = useState<ConfirmAction | null>(null);
  const [signInError] = useState(signInErrorFromUrl);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const sessionQuery = useQuery(orpc.deletePatientData.session.queryOptions({ input: {} }));
  const signedIn = sessionQuery.data?.signedIn === true;

  const facilitiesQuery = useQuery({
    ...orpc.deletePatientData.listFacilities.queryOptions({ input: {} }),
    enabled: signedIn,
  });

  const { pending, revokeOrDeleteSelectedFacilities } = useDeletePatientDataMutations();

  /** Runs every request represented by the confirmed authorized selection. */
  const runConfirmed = async () => {
    if (!confirming || selected.size === 0) return;
    const { kind } = confirming;
    const { outcome, remainingSelected } = await revokeOrDeleteSelectedFacilities(
      confirming,
      organizations,
      selected,
    );
    setSelected(remainingSelected);

    if (outcome.failedCount > 0) {
      toast.error(`${outcome.completedCount} completed and ${outcome.failedCount} failed. Failed items remain selected.`);
      return;
    }
    setConfirming(null);
    toast.success(`${outcome.completedCount} ${kind === "revoke" ? "access request" : "deletion request"}${outcome.completedCount === 1 ? "" : "s"} completed.`);
  };

  const organizations = facilitiesQuery.data?.organizations ?? [];
  // The wait the server will actually apply, so nothing on this page promises a
  // grace period the deployment does not keep.
  const graceDays = facilitiesQuery.data?.graceDays ?? 0;
  const actionableIds = organizations.flatMap((org) =>
    org.facilities.filter(isFacilityActionable).map((facility) => facility.accountId),
  );
  const anyActionable = actionableIds.length > 0;
  const selectedCount = selected.size;
  const allSelected = anyActionable && selectedCount === actionableIds.length;

  /** Opens confirmation using only names and rows returned by the authorized list. */
  const openConfirmation = (kind: ConfirmAction["kind"]): void => {
    setConfirming(deriveConfirmAction(kind, organizations, selected));
  };

  /** Ticks or clears one health system. */
  const toggleFacility = (accountId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(accountId)) next.add(accountId);
      return next;
    });
  };

  /** Ticks every actionable system in one workspace, or clears them all. */
  const toggleOrganization = (organization: (typeof organizations)[number]) => {
    const ids = organization.facilities.filter(isFacilityActionable).map((f) => f.accountId);
    setSelected((current) => {
      const next = new Set(current);
      const everyTicked = ids.length > 0 && ids.every((id) => next.has(id));
      for (const id of ids) {
        if (everyTicked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  /** Ticks everything actionable on the page, or clears the whole selection. */
  const toggleAll = () => {
    setSelected((current) => (current.size === actionableIds.length ? new Set() : new Set(actionableIds)));
  };

  return (
    <main
      data-ph-no-capture
      className="mx-auto flex min-h-screen w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-6 sm:py-12"
    >
      <header className="flex flex-col gap-2">
        <img src="/assets/medblocks.webp" alt="Medblocks" className="size-9 object-contain" />
        <h1 className="text-xl font-semibold sm:text-2xl">Delete your data</h1>
        <p className="max-w-prose text-body text-muted-foreground">
          You can stop future access or permanently remove health records connected through Medblocks.
        </p>
      </header>

      <ol className="flex list-decimal flex-col gap-2 pl-5 text-body text-muted-foreground">
        <li>Search for a hospital, clinic, or insurer and sign in through its patient portal.</li>
        <li>Review the workspaces and health systems linked to that verified portal identity.</li>
        <li>Select what you want to change, then revoke access or delete the stored records.</li>
      </ol>

      {signInError && (
        <p
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive"
        >
          {signInError}
        </p>
      )}

      {sessionQuery.isLoading && (
        <p className="text-body text-muted-foreground">Loading…</p>
      )}

      {!sessionQuery.isLoading && !signedIn && <PortalSignIn />}

      {signedIn && (
        <>
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/40 p-3 sm:flex-row sm:items-center">
            <p className="min-w-0 flex-1 text-caption text-muted-foreground">
              Signed in with{" "}
              <span className="font-medium text-foreground">{sessionQuery.data?.sourceName}</span>.
              These are every workspace and health system linked to that sign-in. Tick the ones you
              want to act on.
            </p>
            {anyActionable ? (
              <Button
                type="button"
                variant="ghost"
                className="h-8 shrink-0 text-caption"
                disabled={pending}
                onClick={toggleAll}
              >
                {allSelected ? "Clear selection" : "Select all"}
              </Button>
            ) : null}
          </div>

          {facilitiesQuery.isLoading ? (
            <p className="text-body text-muted-foreground">Finding your health systems…</p>
          ) : (
            <FacilityList
              organizations={organizations}
              selected={selected}
              pending={pending}
              onToggleFacility={toggleFacility}
              onToggleOrganization={toggleOrganization}
            />
          )}

          {anyActionable ? (
            // Sticky, because the ticks are made down the page and the actions
            // have to stay in reach of wherever the reader got to.
            <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background py-3 sm:flex-row sm:items-center">
              <p className="min-w-0 flex-1 text-caption text-muted-foreground">
                {selectedCount === 0
                  ? "Nothing selected"
                  : `${selectedCount} selected`}
              </p>
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 flex-1 sm:h-9 sm:flex-none"
                  disabled={pending || selectedCount === 0}
                  onClick={() => openConfirmation("revoke")}
                >
                  <ShieldOff aria-hidden />
                  Revoke selected
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-11 flex-1 sm:h-9 sm:flex-none"
                  disabled={pending || selectedCount === 0}
                  onClick={() => openConfirmation("delete")}
                >
                  <Trash2 aria-hidden />
                  Delete selected
                </Button>
              </div>
            </div>
          ) : null}

          <p className="text-meta text-muted-foreground">
            Deleting keeps a record of the request itself, so we can show you what was done. Everything
            else under that health system
            {graceDays === 0
              ? " is removed."
              : ` is fully erased after ${graceDays} ${graceDays === 1 ? "day" : "days"}.`}
          </p>
        </>
      )}

      <ConfirmActionDialog
        action={confirming}
        pending={pending}
        graceDays={graceDays}
        onConfirm={() => { void runConfirmed(); }}
        onCancel={() => { if (!pending) setConfirming(null); }}
      />
    </main>
  );
}
